import { CircleInfoIcon } from "@xola/icons";
import { flash, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import { FormField, requiredString, SwitchField, TextareaField } from "common";
import { findIndex } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Experience, WaitlistPreference } from "types";
import { array, boolean, number, object, string } from "yup";
import { api } from "../../api/api";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useVisibility } from "../../hooks/useVisibility";
import { NumberInlineValuePopover } from "../Common/NumberInlineValuePopover";
import { RadioInput } from "../Common/RadioInput";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLengthCounter } from "../Field/ContentLengthCounter";
import { ContentLayout } from "../Layout/ContentLayout";

enum NotificationStrategy {
    ALL = "all",
    OPEN_SEAT_PERCENTAGE = "open_seat_percentage",
}

const validationSchema = object().shape({
    enabled: boolean().required(),
    enabledForCheckout: boolean().required(),
    enabledForBackOffice: boolean().required(),
    checkoutMessage: requiredString().max(600, "cannot exceed 600 characters"),
    notification: object().shape({
        enabled: boolean().required(),
        strategies: array().of(string()),
        minOpenSeatPercentage: number()
            .min(0, "Must be a number between 0 and 100")
            .max(100, "Must be a number between 0 and 100"),
    }),
});

interface ExperienceWaitlistProps {
    experience: Experience;
}

export const ExperienceWaitlist = ({ experience }: ExperienceWaitlistProps) => {
    const invalidate = useInvalidate();

    const { mutateAsync: updateWaitlistPreference, isLoading: isSavingWaitlistPreference } = useMutation(
        async (payload: WaitlistPreference) => {
            const url = `/experiences/${experience.id}/waitlist`;
            return payload.id ? api.put(url, payload) : api.post(url, payload);
        },
    );

    const handleSubmit = async (payload: WaitlistPreference) => {
        payload.enabled = payload.enabledForBackOffice || payload.enabledForCheckout;
        await updateWaitlistPreference(payload);
        flash.success("Successfully updated experience");
        invalidate("experiences", experience.id);
    };

    return (
        <ContentLayout isWide isDisabled={isSavingWaitlistPreference}>
            <XolaFormik
                validateOnChange={false}
                validationSchema={validationSchema}
                initialValues={getInitialValues(experience.waitlist)}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, handleSubmit, handleReset, errors }) => {
                    return (
                        <>
                            <h2>Waitlist</h2>
                            <div className="divide-y divide-gray-light">
                                <ContentLayout.Section title="Enable Waitlist by Source">
                                    <div className="flex flex-col space-y-2">
                                        <SwitchField
                                            isChecked={values.enabledForBackOffice}
                                            onChange={(value: boolean) => setFieldValue("enabledForBackOffice", value)}
                                        >
                                            <span className="font-bold">Back Office</span>
                                        </SwitchField>
                                        <SwitchField
                                            isChecked={values.enabledForCheckout}
                                            onChange={(value: boolean) => setFieldValue("enabledForCheckout", value)}
                                        >
                                            <span className="font-bold">Checkout</span>
                                        </SwitchField>
                                        {values.enabledForCheckout ? (
                                            <FormField name="checkoutMessage" label="Checkout Message">
                                                <TextareaField rows={5} />
                                                <ContentLengthCounter
                                                    value={values.checkoutMessage ?? ""}
                                                    maxLength={600}
                                                />
                                            </FormField>
                                        ) : null}
                                    </div>
                                </ContentLayout.Section>

                                {values.enabledForBackOffice || values.enabledForCheckout ? (
                                    <WaitListSettingsSection values={values} errors={errors} onChange={setFieldValue} />
                                ) : null}

                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton size="medium" onClick={handleSubmit}>
                                        Save
                                    </SubmitButton>
                                    <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                                </ContentLayout.Footer>

                                <ScrollToFieldError />
                            </div>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

interface WaitListSettingsSectionProps {
    values: WaitlistPreference;
    errors: any;
    onChange: (key: string, value: any) => void;
}

const WaitListSettingsSection = ({ values, errors, onChange }: WaitListSettingsSectionProps) => {
    const [showOpenSeatPercentagePopover, setShowOpenSeatPercentagePopover] = useState(false);
    const [isInviteDelayEnabled, { toggle: toggleInviteDelayEnabled }] = useVisibility(values.inviteDelay > 0);
    const [isInviteDelayValueVisible, { toggle: toggleInviteDelayValueVisible }] = useVisibility(false);
    const [isLimitMaxOpen, { toggle: toggleLimitMax }] = useVisibility(false);

    const handleToggleNotification = (enabled: boolean) => {
        onChange("notification.enabled", enabled);
        if (enabled && !values.notification?.strategies?.[0]) {
            onChange("notification.strategies", [NotificationStrategy.ALL]);
        }
    };

    const handleStrategyChange = (strategy: NotificationStrategy) => {
        const strategies: string[] = [strategy];
        onChange("notification.strategies", strategies);
    };

    const handleNonCriteriaInviteAllChange = (enabled: boolean) => {
        const strategies = [...(values.notification?.strategies ?? [])];
        const index = findIndex(strategies, (strategy) => strategy === NotificationStrategy.ALL);
        if (enabled && index < 0) {
            strategies.push(NotificationStrategy.ALL);
        } else if (!enabled && index >= 0) {
            strategies.splice(index, 1);
        }

        onChange("notification.strategies", strategies);
    };

    const handleInviteDelayChange = (delayValue: number) => {
        onChange("inviteDelay", delayValue);
    };

    const handleInviteDelayToggle = (enabled: boolean) => {
        toggleInviteDelayEnabled();
        const delayValue = enabled ? 2 : 0;
        handleInviteDelayChange(delayValue);
    };

    const handleLimitChange = (enabled: boolean) => {
        onChange("limit.enabled", enabled);
    };

    const handleLimitMaxChange = (value: number) => {
        onChange("limit.max", value);
    };

    const handleCappedEventsToggle = (enabled: boolean) => {
        onChange("enabledForCappedEvents", enabled);
    };

    const hasOpenSeatStrategy = !!values.notification?.strategies.includes(NotificationStrategy.OPEN_SEAT_PERCENTAGE);

    const hasAllStrategy = !hasOpenSeatStrategy && !!values.notification?.strategies.includes(NotificationStrategy.ALL);

    const hasNonCriteriaInviteAllStrategy =
        hasOpenSeatStrategy && !!values.notification?.strategies.includes(NotificationStrategy.ALL);

    return (
        <ContentLayout.Section title="Settings" description="">
            <div className="flex flex-col space-y-2">
                <Switch.Group>
                    <Switch isChecked={values.limit?.enabled} onChange={handleLimitChange} />
                    <Switch.Label direction="right" className="ml-3">
                        Limit waitlist spots
                    </Switch.Label>
                    {values.limit?.enabled && (
                        <>
                            &nbsp;to
                            <NumberInlineValuePopover
                                isOpen={isLimitMaxOpen}
                                error={errors?.refundPercentage}
                                text={`${values.limit.max ?? 0}`}
                                value={values.limit.max}
                                onChange={handleLimitMaxChange}
                                onOpenChange={toggleLimitMax}
                            />
                            per event
                        </>
                    )}
                    <Tooltip
                        content="Restricts the number of guests that can join the waitlist per event. Enabling this feature will also limit guests to a single timeslot when joining a waitlist (they will no longer be able to choose a time range)."
                        className="ml-2"
                    >
                        <CircleInfoIcon />
                    </Tooltip>
                </Switch.Group>
                <div className="flex flex-col space-y-2">
                    <SwitchField isChecked={values.notification?.enabled ?? false} onChange={handleToggleNotification}>
                        <span className="space-x-2 font-bold">
                            <span>
                                Automatically send purchase invites {values.notification?.enabled ? "to" : null}
                            </span>
                            <Tooltip content="Invites will not be sent after the early cut off">
                                <CircleInfoIcon />
                            </Tooltip>
                        </span>
                    </SwitchField>
                    {values.notification?.enabled ? (
                        <div className="ml-10 space-y-2">
                            <RadioInput
                                checked={hasAllStrategy}
                                onChange={() => handleStrategyChange(NotificationStrategy.ALL)}
                            >
                                All waitlisted groups that can fit the open spots
                            </RadioInput>
                            <RadioInput
                                checked={hasOpenSeatStrategy}
                                onChange={() => handleStrategyChange(NotificationStrategy.OPEN_SEAT_PERCENTAGE)}
                            >
                                <span>
                                    Groups who could fill at least of
                                    <NumberInlineValuePopover
                                        isOpen={showOpenSeatPercentagePopover}
                                        value={values.notification.minOpenSeatPercentage}
                                        text={`${values.notification.minOpenSeatPercentage ?? 0} %`}
                                        min="0"
                                        error={errors?.notification?.minOpenSeatPercentage}
                                        disabled={hasAllStrategy}
                                        onOpenChange={setShowOpenSeatPercentagePopover}
                                        onChange={(value: number) =>
                                            onChange("notification.minOpenSeatPercentage", value)
                                        }
                                    />
                                    open spots
                                </span>
                            </RadioInput>
                            {hasOpenSeatStrategy ? (
                                <div className="ml-4">
                                    <SwitchField
                                        isChecked={hasNonCriteriaInviteAllStrategy}
                                        onChange={handleNonCriteriaInviteAllChange}
                                    >
                                        If criteria not met, invite all waitlisted groups that can fit the open spots
                                    </SwitchField>
                                </div>
                            ) : null}
                            {values.notification?.enabled ? (
                                <div>
                                    <SwitchField isChecked={isInviteDelayEnabled} onChange={handleInviteDelayToggle}>
                                        <span onClick={(e) => e.stopPropagation()}>
                                            Delay first invitation by
                                            <NumberInlineValuePopover
                                                isOpen={isInviteDelayValueVisible}
                                                disabled={!isInviteDelayEnabled}
                                                value={values.inviteDelay}
                                                text={`${values.inviteDelay ? values.inviteDelay : 0}`}
                                                onChange={handleInviteDelayChange}
                                                onOpenChange={toggleInviteDelayValueVisible}
                                            />
                                            minutes
                                        </span>
                                    </SwitchField>
                                    <Tooltip
                                        content={
                                            values.inviteDelay > 0 ? existingWaitlistDelayMessage : waitlistDelayMessage
                                        }
                                        className="ml-2"
                                    >
                                        <CircleInfoIcon />
                                    </Tooltip>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    <div>
                        <SwitchField isChecked={values.enabledForCappedEvents} onChange={handleCappedEventsToggle}>
                            <span>Allow waitlist on events where capacity has been manually capped</span>
                            <span>
                                <Tooltip content="Applies only to checkout" className="ml-2">
                                    <CircleInfoIcon />
                                </Tooltip>
                            </span>
                        </SwitchField>
                    </div>
                </div>
            </div>
        </ContentLayout.Section>
    );
};

const getInitialValues = (waitlistPreference?: WaitlistPreference) => {
    return (
        waitlistPreference ?? {
            enabled: false,
            enabledForBackOffice: false,
            enabledForCheckout: false,
            enabledForCappedEvents: false,
            checkoutMessage:
                "This timeslot has either been fully booked or cannot accommodate your selected group size. You may opt to join a waitlist and if spots open up, you will be notified via email.",
            inviteDelay: 0,
            notification: {
                enabled: false,
                strategies: [],
            },
            limit: { enabled: false },
        }
    );
};

const waitlistDelayMessage =
    "Waitlist invitations are currently sent immediately after waitlisted spots open up. Turning this on will delay invitation emails by the number of minutes you specify. This is generally useful if you open up extra spots to accommodate a specific group of guests.";

const existingWaitlistDelayMessage =
    "Waitlist invitations are currently delayed by the specified number of minutes. If disabled, waitlist invitation emails will be sent to guests immediately after waitlisted slots open up.";
